import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, FileText, ArrowLeft, Lock, CheckCircle2, XCircle, Plus, Eye, Download, Mail } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  draft:    { label: 'Draft',    cls: 'border-gray-300 bg-gray-50 text-gray-700' },
  sent:     { label: 'Sent',     cls: 'border-blue-300 bg-blue-50 text-blue-700' },
  accepted: { label: 'Accepted', cls: 'border-green-300 bg-green-50 text-green-700' },
  declined: { label: 'Declined', cls: 'border-red-300 bg-red-50 text-red-600' },
};

const BUILDING_TYPES = [
  { value: 'house', label: 'House' },
  { value: 'apartment', label: 'Apartment / Multi-unit' },
  { value: 'office', label: 'Office' },
  { value: 'retail', label: 'Retail' },
  { value: 'school', label: 'School / Education' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'warehouse', label: 'Warehouse / Industrial' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'outbuilding', label: 'Outbuilding / Garage' },
];

const PROJECT_TYPES = [
  { value: 'new_build', label: 'New build' },
  { value: 'renovation', label: 'Renovation' },
  { value: 'addition', label: 'Addition / Alteration' },
  { value: 'advisory', label: 'Advisory only' },
];

const zar = (n) => 'R' + Number(n || 0).toLocaleString('en-ZA', { maximumFractionDigits: 0 });

// ================== LIST VIEW ==================
function ProposalsList({ onOpen, onCreate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/studio/proposals');
      setRows(data);
    } catch { toast.error('Failed to load proposals'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() =>
    statusFilter === 'all' ? rows : rows.filter(r => r.status === statusFilter)
  , [rows, statusFilter]);

  return (
    <div data-testid="proposals-list">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading text-xl font-medium text-brand-dark">Proposals</h2>
          <p className="text-sm text-brand-dark/50 font-body mt-0.5">
            Engagement letters and fee proposals — the Inception gate.
          </p>
        </div>
        <Button size="sm" onClick={onCreate} data-testid="proposal-create-btn"
          className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> New Proposal
        </Button>
      </div>

      <div className="flex gap-2 mb-4">
        {['all', 'draft', 'sent', 'accepted', 'declined'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`proposal-filter-${s}`}
            className={`px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-wider font-body border transition-colors ${
              statusFilter === s ? 'bg-brand-dark text-white border-brand-dark' : 'bg-white text-brand-dark/60 border-black/10 hover:border-brand-dark/30'
            }`}>
            {s === 'all' ? `All (${rows.length})` : `${s} (${rows.filter(r => r.status === s).length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center text-brand-dark/40"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-sm border border-black/10 p-10 text-center">
          <FileText className="w-8 h-8 text-brand-dark/20 mx-auto mb-3" />
          <p className="text-brand-dark/50 font-body text-sm">No proposals {statusFilter !== 'all' ? `in ${statusFilter}` : 'yet'}.</p>
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-black/10 overflow-hidden" data-testid="proposals-table">
          <table className="w-full text-sm font-body">
            <thead className="bg-brand-light/50 text-[10px] uppercase tracking-wider text-brand-dark/40">
              <tr>
                <th className="text-left px-4 py-2.5">Proposal #</th>
                <th className="text-left px-4 py-2.5">Client</th>
                <th className="text-left px-4 py-2.5">Project</th>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-right px-4 py-2.5">Fee</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const s = STATUS_STYLES[p.status] || STATUS_STYLES.draft;
                return (
                  <tr key={p.proposal_id} onClick={() => onOpen(p)} data-testid={`proposal-row-${p.proposal_id}`}
                    className="border-t border-black/5 hover:bg-brand-light/30 cursor-pointer">
                    <td className="px-4 py-3 font-medium text-brand-dark">{p.proposal_number}</td>
                    <td className="px-4 py-3 text-brand-dark/70">{p.client_name}</td>
                    <td className="px-4 py-3 text-brand-dark/70">{p.project_name}</td>
                    <td className="px-4 py-3 text-brand-dark/50 text-xs capitalize">{p.building_type} · {p.project_type?.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-right font-medium text-brand-dark">{zar(p.fee?.adjusted_fee)}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`text-[10px] ${s.cls}`}>{s.label}</Badge>
                    </td>
                    <td className="px-4 py-3 text-brand-dark/40 text-xs">
                      {new Date(p.created_at).toLocaleDateString('en-ZA')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ================== CREATE DIALOG ==================
function CreateProposalDialog({ open, onClose, onCreated, prefilled }) {
  const [form, setForm] = useState({
    client_name: '', project_name: '', building_type: 'house',
    project_type: 'new_build', cost_of_works: '',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        client_name: prefilled?.client_name || '',
        project_name: prefilled?.project_name || '',
        building_type: prefilled?.building_type || 'house',
        project_type: prefilled?.project_type || 'new_build',
        cost_of_works: prefilled?.cost_of_works || '',
        lead_id: prefilled?.lead_id,
        project_id: prefilled?.project_id,
      });
    }
  }, [open, prefilled]);

  if (!open) return null;

  const submit = async () => {
    if (!form.client_name || !form.project_name || !form.cost_of_works) {
      toast.error('Client, project name and cost of works are required');
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/studio/proposals', {
        ...form,
        cost_of_works: Number(form.cost_of_works),
      });
      toast.success(`Proposal ${data.proposal_number} created`);
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    } finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="proposal-create-dialog">
      <div className="bg-white rounded-sm border border-black/10 max-w-lg w-full p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-brand-dark mb-4">New Proposal</h3>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Client Name</Label>
            <Input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} className="h-9 rounded-sm text-sm" data-testid="proposal-create-client" />
          </div>
          <div>
            <Label className="text-xs">Project Name</Label>
            <Input value={form.project_name} onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))} className="h-9 rounded-sm text-sm" data-testid="proposal-create-project-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Building Type</Label>
              <Select value={form.building_type} onValueChange={v => setForm(f => ({ ...f, building_type: v }))}>
                <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="proposal-create-building-type"><SelectValue /></SelectTrigger>
                <SelectContent>{BUILDING_TYPES.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Project Type</Label>
              <Select value={form.project_type} onValueChange={v => setForm(f => ({ ...f, project_type: v }))}>
                <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="proposal-create-project-type"><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_TYPES.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Estimated Cost of Works (ZAR)</Label>
            <Input type="number" value={form.cost_of_works} onChange={e => setForm(f => ({ ...f, cost_of_works: e.target.value }))} className="h-9 rounded-sm text-sm" placeholder="e.g. 5 500 000" data-testid="proposal-create-cost" />
            <p className="text-[11px] text-brand-dark/40 mt-1 font-body">Fee scale is applied from Settings → Practice Configuration.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button size="sm" variant="ghost" onClick={onClose} disabled={creating} data-testid="proposal-create-cancel">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={creating} data-testid="proposal-create-submit"
            className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Create draft'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ================== DETAIL / BUILDER ==================
function ProposalDetail({ proposal, onBack, onRefresh }) {
  const [local, setLocal] = useState(proposal);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(null); // 'send-email'|'mark-sent'|'accept'|'decline'
  const [declineReason, setDeclineReason] = useState('');
  const [sendForm, setSendForm] = useState({ to: '', note: '' });
  const [sendWorking, setSendWorking] = useState(false);

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/studio/proposals/${proposal.proposal_id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Raum_Proposal_${proposal.proposal_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const previewPdf = async () => {
    try {
      const resp = await api.get(`/studio/proposals/${proposal.proposal_id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after a moment so the new tab has time to load
      setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
    } catch { toast.error('Preview failed'); }
  };

  const doSendEmail = async () => {
    if (!sendForm.to || !/@/.test(sendForm.to)) {
      toast.error('Valid client email required');
      return;
    }
    setSendWorking(true);
    try {
      await api.post(`/studio/proposals/${proposal.proposal_id}/send-email`, {
        to: sendForm.to.trim().toLowerCase(),
        note: sendForm.note,
      });
      toast.success(`Proposal emailed to ${sendForm.to}`);
      setConfirming(null);
      setSendForm({ to: '', note: '' });
      onRefresh?.();
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email delivery failed');
    } finally { setSendWorking(false); }
  };

  useEffect(() => { setLocal(proposal); }, [proposal]);

  const locked = local.locked || ['accepted', 'declined'].includes(local.status);

  const updateLetter = (field, value) => {
    setLocal(p => ({ ...p, letter: { ...(p.letter || {}), [field]: value } }));
  };

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/studio/proposals/${local.proposal_id}`, patch || { letter: local.letter, fee_notes: local.fee_notes });
      setLocal(data);
      toast.success('Proposal saved');
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const recomputeFee = async (overrides) => {
    await save(overrides);
  };

  const runStatus = async (action) => {
    try {
      if (action === 'decline') {
        await api.post(`/studio/proposals/${local.proposal_id}/decline`, { reason: declineReason });
        toast.success('Proposal declined — lead marked lost');
      } else if (action === 'mark-sent') {
        await api.post(`/studio/proposals/${local.proposal_id}/send`);
        toast.success('Proposal marked as sent');
      } else if (action === 'accept') {
        await api.post(`/studio/proposals/${local.proposal_id}/accept`);
        toast.success('Proposal accepted — project in motion');
      }
      setConfirming(null);
      onRefresh?.();
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const openSendDialog = async () => {
    // Prefill recipient from linked lead email if we have one
    let prefilled = '';
    if (local.lead_id) {
      try {
        const { data } = await api.get('/studio/leads');
        const lead = (Array.isArray(data) ? data : []).find(l => l.lead_id === local.lead_id);
        if (lead?.email) prefilled = lead.email;
      } catch { /* noop */ }
    }
    setSendForm({ to: prefilled, note: '' });
    setConfirming('send-email');
  };

  const s = STATUS_STYLES[local.status] || STATUS_STYLES.draft;

  return (
    <div data-testid="proposal-detail">
      <Button variant="ghost" onClick={onBack} className="mb-3 text-sm" data-testid="proposal-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to Proposals
      </Button>

      <div className="bg-white rounded-sm border border-black/10 p-6 mb-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-brand-dark/40 font-body">{local.proposal_number}</p>
            <h2 className="font-heading text-xl font-medium text-brand-dark" data-testid="proposal-detail-name">{local.project_name}</h2>
            <p className="text-sm text-brand-dark/60 font-body">{local.client_name}</p>
            {local.project_id && (
              <p className="text-[11px] text-brand-dark/40 font-body mt-1">Linked to project {local.project_id.slice(0, 8)}…</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`${s.cls}`}>{s.label}</Badge>
            {locked && <Lock className="w-3.5 h-3.5 text-brand-dark/40" />}
          </div>
        </div>

        {/* Action bar — Preview + Download always available; Send/Accept/Decline when appropriate */}
        <div className="flex flex-wrap gap-2 pt-3 border-t border-black/5">
          <Button size="sm" variant="outline" onClick={previewPdf} data-testid="proposal-preview-btn"
            className="rounded-sm font-body text-xs uppercase tracking-wider border-black/15 text-brand-dark hover:bg-brand-light">
            <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
          </Button>
          <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="proposal-download-btn"
            className="rounded-sm font-body text-xs uppercase tracking-wider border-black/15 text-brand-dark hover:bg-brand-light">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
          </Button>

          {!locked && local.status === 'draft' && (
            <>
              <Button size="sm" onClick={openSendDialog} data-testid="proposal-send-email-btn"
                className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
                <Mail className="w-3.5 h-3.5 mr-1.5" /> Send to client
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming('mark-sent')} data-testid="proposal-mark-sent-btn"
                className="text-xs text-brand-dark/60 hover:text-brand-dark">
                Mark as sent (sent elsewhere)
              </Button>
            </>
          )}
          {!locked && local.status === 'sent' && (
            <>
              <Button size="sm" onClick={() => setConfirming('accept')} data-testid="proposal-accept-btn"
                className="bg-green-700 text-white hover:bg-green-800 rounded-sm font-body text-xs uppercase tracking-wider">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark accepted
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConfirming('decline')} data-testid="proposal-decline-btn"
                className="rounded-sm font-body text-xs uppercase tracking-wider text-red-600 border-red-200 hover:bg-red-50">
                <XCircle className="w-3.5 h-3.5 mr-1.5" /> Mark declined
              </Button>
            </>
          )}
          <div className="flex-1" />
          {!locked && (
            <Button size="sm" variant="ghost" onClick={() => save()} disabled={saving} data-testid="proposal-save-btn" className="text-xs">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save changes'}
            </Button>
          )}
        </div>
        {locked && local.status === 'declined' && local.decline_reason && (
          <div className="mt-3 pt-3 border-t border-black/5 text-xs text-brand-dark/60 font-body italic">
            Declined: {local.decline_reason}
          </div>
        )}
      </div>

      <Tabs defaultValue="letter" className="bg-white rounded-sm border border-black/10">
        <TabsList className="w-full justify-start border-b border-black/5 rounded-none bg-transparent h-auto p-0">
          <TabsTrigger value="letter" data-testid="proposal-tab-letter"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-accent data-[state=active]:bg-transparent px-5 py-3 text-xs uppercase tracking-wider">
            1 · Engagement Letter
          </TabsTrigger>
          <TabsTrigger value="fee" data-testid="proposal-tab-fee"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-accent data-[state=active]:bg-transparent px-5 py-3 text-xs uppercase tracking-wider">
            2 · Fee Proposal
          </TabsTrigger>
        </TabsList>

        {/* LETTER */}
        <TabsContent value="letter" className="p-6 space-y-5" data-testid="proposal-letter-content">
          {[
            { k: 'preamble', label: 'Preamble' },
            { k: 'appointment_basis', label: 'Appointment Basis' },
            { k: 'conditions_of_engagement', label: 'Conditions of Engagement' },
            { k: 'sacap_basis', label: 'SACAP Fee Basis' },
            { k: 'payment_terms', label: 'Payment Terms' },
            { k: 'exclusions', label: 'Exclusions' },
            { k: 'client_obligations', label: 'Client Obligations' },
          ].map(({ k, label }) => (
            <div key={k}>
              <Label className="text-[11px] uppercase tracking-wider text-brand-dark/50">{label}</Label>
              <Textarea value={(local.letter || {})[k] || ''}
                onChange={e => updateLetter(k, e.target.value)}
                disabled={locked}
                className="rounded-sm text-sm min-h-[80px] mt-1"
                data-testid={`proposal-letter-${k}`} />
            </div>
          ))}
          {/* Stage descriptions — canonical, not editable */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-brand-dark/50">Project Stages & Activities</Label>
            <p className="text-[11px] text-brand-dark/40 font-body mt-0.5 mb-2">Practice-standard scope per stage — governed centrally.</p>
            <div className="space-y-3">
              {(local.letter?.stages || []).map((st, i) => (
                <div key={st.stage_key} className="border border-black/5 rounded-sm p-3 bg-brand-light/30" data-testid={`proposal-letter-stage-${st.stage_key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-brand-dark">{i + 1}. {st.stage_name}</p>
                    <span className="text-xs text-brand-dark/40">{st.pct}% of fee</span>
                  </div>
                  <ul className="list-disc ml-5 text-xs text-brand-dark/60 space-y-0.5">
                    {st.activities.map(a => <li key={a}>{a}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* FEE */}
        <TabsContent value="fee" className="p-6 space-y-5" data-testid="proposal-fee-content">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Building Type</Label>
              <Select value={local.building_type} disabled={locked}
                onValueChange={v => recomputeFee({ building_type: v })}>
                <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="proposal-fee-building-type"><SelectValue /></SelectTrigger>
                <SelectContent>{BUILDING_TYPES.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Project Type</Label>
              <Select value={local.project_type} disabled={locked}
                onValueChange={v => recomputeFee({ project_type: v })}>
                <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="proposal-fee-project-type"><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_TYPES.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Cost of Works (ZAR)</Label>
              <Input type="number" value={local.cost_of_works}
                onChange={e => setLocal(p => ({ ...p, cost_of_works: Number(e.target.value) }))}
                onBlur={e => recomputeFee({ cost_of_works: Number(e.target.value) })}
                disabled={locked}
                className="h-9 rounded-sm text-sm" data-testid="proposal-fee-cost" />
            </div>
            <div>
              <Label className="text-xs">Risk Uplift %</Label>
              <Input type="number" step="1" value={local.risk_uplift_pct}
                onChange={e => setLocal(p => ({ ...p, risk_uplift_pct: Number(e.target.value) }))}
                onBlur={e => recomputeFee({ risk_uplift_pct: Number(e.target.value) })}
                disabled={locked}
                className="h-9 rounded-sm text-sm" data-testid="proposal-fee-uplift" />
            </div>
          </div>

          <div className="border border-black/5 rounded-sm overflow-hidden" data-testid="proposal-fee-apportionment">
            <table className="w-full text-sm font-body">
              <thead className="bg-brand-light/50 text-[10px] uppercase tracking-wider text-brand-dark/40">
                <tr>
                  <th className="text-left px-3 py-2 w-8">#</th>
                  <th className="text-left px-3 py-2">Stage</th>
                  <th className="text-right px-3 py-2 w-20">%</th>
                  <th className="text-right px-3 py-2">Amount (ZAR)</th>
                </tr>
              </thead>
              <tbody>
                {(local.fee?.apportionment || []).map((row, i) => (
                  <tr key={row.stage_key} className="border-t border-black/5" data-testid={`proposal-fee-row-${row.stage_key}`}>
                    <td className="px-3 py-2 text-xs text-brand-dark/40">{i + 1}</td>
                    <td className="px-3 py-2 text-brand-dark/80">{row.stage_name}</td>
                    <td className="px-3 py-2 text-right text-brand-dark/60">{row.pct}%</td>
                    <td className="px-3 py-2 text-right font-medium text-brand-dark">{zar(row.amount)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-brand-accent/30 bg-brand-light/40">
                  <td className="px-3 py-3" />
                  <td className="px-3 py-3 text-sm font-semibold text-brand-dark uppercase tracking-wider">Total Fee</td>
                  <td className="px-3 py-3 text-right text-xs text-brand-dark/40">100%</td>
                  <td className="px-3 py-3 text-right text-lg font-semibold text-brand-dark" data-testid="proposal-fee-total">
                    {zar(local.fee?.adjusted_fee)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-brand-dark/50 font-body">
            <div>Fee from scale: <span className="text-brand-dark/80 font-medium">{zar(local.fee?.fee_from_scale)}</span></div>
            <div>Risk uplift applied: <span className="text-brand-dark/80 font-medium">+{local.fee?.risk_uplift_pct}%</span></div>
            <div>Scale discount: <span className="text-brand-dark/80 font-medium">× {local.fee?.discount_applied}</span></div>
          </div>

          <div>
            <Label className="text-xs">Clarifying notes (architect)</Label>
            <Textarea value={local.fee_notes || ''}
              onChange={e => setLocal(p => ({ ...p, fee_notes: e.target.value }))}
              onBlur={() => save()}
              disabled={locked}
              className="rounded-sm text-sm min-h-[70px] mt-1" placeholder="Optional — e.g. excludes specialist fees, assumes single-storey scope..."
              data-testid="proposal-fee-notes" />
          </div>

          <p className="text-[11px] text-brand-dark/40 italic font-body">
            Core calculations (sliding-scale brackets and discount factor) are governed from Settings → Practice Configuration.
          </p>
        </TabsContent>
      </Tabs>

      {/* Confirmation modals */}
      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="proposal-confirm-dialog">
          <div className="bg-white rounded-sm border border-black/10 max-w-md w-full p-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-brand-dark mb-2">
              {confirming === 'send-email' && 'Send proposal to client'}
              {confirming === 'mark-sent'  && 'Mark proposal as sent?'}
              {confirming === 'accept'     && 'Mark proposal accepted?'}
              {confirming === 'decline'    && 'Mark proposal declined?'}
            </h3>
            <p className="text-sm text-brand-dark/60 font-body mb-4">
              {confirming === 'send-email' && 'The proposal PDF will be attached and emailed to the client. Once delivered, the proposal moves to Sent status.'}
              {confirming === 'mark-sent' && 'Use this if you have sent the proposal outside the app. Moves status to Sent. The proposal remains editable until accepted or declined.'}
              {confirming === 'accept'     && 'Accepting locks the proposal and opens the project in Inception. The fee basis carries forward.'}
              {confirming === 'decline'    && 'Declining marks the linked lead/project as lost. This cannot be undone.'}
            </p>

            {confirming === 'send-email' && (
              <div className="space-y-3 mb-4">
                <div>
                  <Label className="text-xs">Client email</Label>
                  <Input type="email" value={sendForm.to}
                    onChange={e => setSendForm(f => ({ ...f, to: e.target.value }))}
                    placeholder="client@example.com"
                    className="h-9 rounded-sm text-sm" data-testid="proposal-send-to" />
                </div>
                <div>
                  <Label className="text-xs">Personal note (optional)</Label>
                  <Textarea value={sendForm.note}
                    onChange={e => setSendForm(f => ({ ...f, note: e.target.value }))}
                    placeholder="Short message shown above the attached proposal..."
                    className="rounded-sm text-sm min-h-[70px]" data-testid="proposal-send-note" />
                </div>
                <p className="text-[11px] text-brand-dark/40 font-body">
                  Email is sent from Raum Architects via the configured email provider.
                </p>
              </div>
            )}

            {confirming === 'decline' && (
              <div className="mb-4">
                <Label className="text-xs">Reason (optional)</Label>
                <Textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} className="rounded-sm text-sm" data-testid="proposal-decline-reason" />
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirming(null)} data-testid="proposal-confirm-cancel"
                disabled={sendWorking}>
                Cancel
              </Button>
              {confirming === 'send-email' ? (
                <Button size="sm" onClick={doSendEmail} disabled={sendWorking} data-testid="proposal-confirm-send-email"
                  className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
                  {sendWorking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Mail className="w-3.5 h-3.5 mr-1.5" /> Send now</>}
                </Button>
              ) : (
                <Button size="sm" onClick={() => runStatus(confirming)} data-testid="proposal-confirm-submit"
                  className={confirming === 'decline' ? 'bg-red-600 hover:bg-red-700 text-white rounded-sm font-body text-xs uppercase tracking-wider' : 'bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider'}>
                  Confirm
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ================== CONTAINER ==================
export default function ProposalsStudio({ prefilledCreate, onPrefillConsumed }) {
  const [current, setCurrent] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // Open the create modal if a prefill was passed in from Leads / Projects
  useEffect(() => {
    if (prefilledCreate) {
      setShowCreate(true);
    }
  }, [prefilledCreate]);

  const bumpRefresh = () => setRefreshTick(t => t + 1);

  const openProposal = async (p) => {
    try {
      const { data } = await api.get(`/studio/proposals/${p.proposal_id}`);
      setCurrent(data);
    } catch { toast.error('Failed to open proposal'); }
  };

  if (current) {
    return (
      <ProposalDetail
        proposal={current}
        onBack={() => { setCurrent(null); bumpRefresh(); }}
        onRefresh={async () => {
          const { data } = await api.get(`/studio/proposals/${current.proposal_id}`);
          setCurrent(data);
        }}
      />
    );
  }

  return (
    <>
      <ProposalsList
        key={refreshTick}
        onOpen={openProposal}
        onCreate={() => setShowCreate(true)}
      />
      <CreateProposalDialog
        open={showCreate}
        prefilled={prefilledCreate}
        onClose={() => { setShowCreate(false); onPrefillConsumed?.(); }}
        onCreated={(p) => { setShowCreate(false); onPrefillConsumed?.(); openProposal(p); }}
      />
    </>
  );
}
